import { useAdminLogin } from '@/hooks/useAdminSettings';
import { loginSchema, type LoginFormData } from '@/schemas/settings.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Shield } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export function AdminLoginForm() {
  const navigate = useNavigate();
  const loginMutation = useAdminLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data, {
      onSuccess: (response) => {
        const r = response?.data ?? response;
        const user = r?.user ?? r;
        if (user?.role === 'super_admin' || user?.role === 'admin') {
          navigate('/');
        }
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Email
        </label>
        <input
          {...register('email')}
          type="email"
          id="email"
          className="focus:border-brand-500 focus:ring-brand-100 dark:focus:ring-brand-500/20 mt-1 block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 shadow-sm transition-colors focus:ring-2 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
          placeholder="admin@innov-paie.fr"
        />
        {errors.email && (
          <p className="text-error-600 mt-1 text-sm">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Mot de passe
        </label>
        <input
          {...register('password')}
          type="password"
          id="password"
          className="focus:border-brand-500 focus:ring-brand-100 dark:focus:ring-brand-500/20 mt-1 block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 shadow-sm transition-colors focus:ring-2 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
          placeholder="Votre mot de passe"
        />
        {errors.password && (
          <p className="text-error-600 mt-1 text-sm">{errors.password.message}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loginMutation.isPending}
        className="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 flex w-full justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
      >
        {loginMutation.isPending ? 'Connexion...' : 'Se connecter'}
      </button>

      {/* Admin badge */}
      <div className="flex items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-500/20 dark:bg-primary-500/10">
        <Shield className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        <span className="text-xs text-primary-700 dark:text-primary-300">
          Espace sécurisé — Accès réservé aux administrateurs
        </span>
      </div>
    </form>
  );
}
